import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { useAppSelector } from 'hooks/useAppSelector';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useToggleFeature } from 'hooks/useToggleFeature';
import type { IntlKey } from 'locales';
import { type ChangeEvent, type ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { Region } from 'state/discover/page/page.model';
import * as fromUser from 'state/user/user.selector';
import type {
  BatchWorkspaceVersion,
  LandingPage,
  QuoteWorkspaceVersion,
} from 'state/userPreferences/userPreferences.model';
import { pascalCaseToWords } from 'utils/string';

import { FlexRowGroup } from './RowGroup';
import { SectionTitle, TabForm } from './TabForm';

interface GeneralTabProps {
  children: ReactNode;
  onClose: () => void;

  landingPage?: LandingPage;
  onLandingPageChanged: (value: LandingPage) => void;

  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  onQuoteWorkspaceVersionChanged: (value: QuoteWorkspaceVersion) => void;

  batchWorkspaceVersion: BatchWorkspaceVersion;
  onBatchWorkspaceVersionChanged: (value: BatchWorkspaceVersion) => void;

  discoverPageRegion?: Region;
  onDiscoverPageRegionChanged: (value: Region) => void;
  availableDiscoverPageRegions: readonly Region[];
}

function openAccountCenterThemeSwitcher() {
  const accountCenter = document.querySelector('sgwt-account-center');
  const accCtrBtn = accountCenter?.querySelector<HTMLButtonElement>('.sgwt-account-center-user-info');
  accCtrBtn?.click();
  setTimeout(() => {
    const themeSwitcherButton = accountCenter?.querySelector<HTMLButtonElement>(
      '.sgwt-account-center-user-panel-theme-button',
    );
    themeSwitcherButton?.click();
  });
}

export function GeneralTabForm(props: GeneralTabProps) {
  const essentialWorkspaceActivated = useToggleFeature('essentialWorkspaceParis');

  return (
    <>
      {essentialWorkspaceActivated && <GeneralTabFormNew {...props} />}
      {!essentialWorkspaceActivated && <GeneralTabFormOld {...props} />}
    </>
  );
}

function translateLandingPageToTrackingName(value: LandingPage) {
  switch (value) {
    case 'EssentialWorkspace':
      return 'pricing';
    case 'QuoteWorkspace':
      return 'pricing';
    case 'WelcomePage':
      return 'discover';
  }
}

function GeneralTabFormNew({
  onClose,
  landingPage,
  onLandingPageChanged,
  batchWorkspaceVersion,
  onBatchWorkspaceVersionChanged,
  discoverPageRegion,
  onDiscoverPageRegionChanged,
  availableDiscoverPageRegions,
  children,
}: GeneralTabProps) {
  const { formatMessage } = useIntl();
  const isInternalUser = useIsInternalUser();
  const hasWelcomePage = useAppSelector(fromUser.isDiscoverPageEnabledForUser);
  const hasPrimaryQuote = useAppSelector(fromUser.hasPrimaryQuote);

  const landingPagePossibleItems: LandingPage[] = useMemo(() => {
    const items: LandingPage[] = [];
    if (hasWelcomePage) {
      items.push('WelcomePage');
    }
    if (hasPrimaryQuote) {
      items.push('EssentialWorkspace', 'QuoteWorkspace');
    }
    return items;
  }, [hasWelcomePage, hasPrimaryQuote]);
  const onLandingPageValueChanged = useCallback(
    (value: LandingPage) => {
      const trackingName = translateLandingPageToTrackingName(value);
      logAnalytics.trackEvent('User preferences', 'Set default landing page', trackingName);
      onLandingPageChanged(value);
    },
    [onLandingPageChanged],
  );
  const onDiscoverPageRegionValueChanged = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      logAnalytics.trackEvent('User preferences', 'Set default discover page region', value);
      onDiscoverPageRegionChanged(value as Region);
    },
    [onDiscoverPageRegionChanged],
  );

  return (
    <TabForm labelKey="userPreferencesForm.general.label" onClose={onClose}>
      <SectionTitle labelKey="userPreferencesForm.displayOptions.label" />
      <FlexRowGroup labelKey="userPreferencesForm.isDarkMode.label" isFirst inputId="theme">
        <div className="d-flex align-items-center">
          <FormattedMessage id="userPreferencesForm.theme.warning" />
          <button
            type="button"
            className="btn btn-flat-primary btn-icon-end ms-2"
            onClick={() => {
              onClose();
              openAccountCenterThemeSwitcher();
            }}
          >
            <FormattedMessage id="userPreferencesForm.theme.link" />
            <i className="icon">open_in_new</i>
          </button>
        </div>
      </FlexRowGroup>

      {landingPagePossibleItems.length > 0 && (
        <>
          <SectionTitle labelKey="userPreferencesForm.landingPage.label" />
          <FlexRowGroup labelKey="userPreferencesForm.landingPage.default.label" isFirst>
            <ButtonsGroup
              className="w-100"
              items={landingPagePossibleItems}
              selectedItems={landingPage ?? 'WelcomePage'}
              onSelect={items => onLandingPageValueChanged(items[0])}
              isRadio
              translationPrefix="userPreferencesForm.landingPage.values"
            />
          </FlexRowGroup>
        </>
      )}
      <SectionTitle labelKey="userPreferencesForm.batchWorkspaceVersion.label" />
      <FlexRowGroup labelKey="userPreferencesForm.batchWorkspaceVersion.default.label" isFirst>
        <ButtonsGroup
          className="w-100"
          items={['Standard', 'Advanced']}
          selectedItems={batchWorkspaceVersion}
          onSelect={items => onBatchWorkspaceVersionChanged(items[0])}
          isRadio
          translationPrefix="userPreferencesForm.batchWorkspaceVersion.values"
        />
      </FlexRowGroup>
      {hasWelcomePage && isInternalUser && (
        <>
          <SectionTitle labelKey="userPreferencesForm.discoverPageRegion.label" />
          <FlexRowGroup labelKey="userPreferencesForm.discoverPageRegion.default.label" isFirst>
            <div className="form-group mb-0 me-1">
              <select
                data-e2e="region-selector"
                value={discoverPageRegion ?? 'Switzerland'}
                onChange={onDiscoverPageRegionValueChanged}
                className="form-select"
              >
                {availableDiscoverPageRegions.map(item => (
                  <option value={item} key={item}>
                    {formatMessage({
                      id: `discover.region.${item}` as IntlKey,
                      defaultMessage: pascalCaseToWords(item),
                    })}
                  </option>
                ))}
              </select>
            </div>
          </FlexRowGroup>
        </>
      )}
      {children}
    </TabForm>
  );
}

export function GeneralTabFormOld({
  onClose,
  landingPage,
  onLandingPageChanged,
  quoteWorkspaceVersion,
  onQuoteWorkspaceVersionChanged,
  batchWorkspaceVersion,
  onBatchWorkspaceVersionChanged,
  discoverPageRegion,
  onDiscoverPageRegionChanged,
  availableDiscoverPageRegions,
  children,
}: GeneralTabProps) {
  const { formatMessage } = useIntl();
  const isInternalUser = useIsInternalUser();
  const hasWelcomePage = useAppSelector(fromUser.isDiscoverPageEnabledForUser);
  const onLandingPageValueChanged = useCallback(
    (value: string) => {
      if (value === 'WelcomePage') {
        logAnalytics.trackEvent('User preferences', 'Set default landing page', 'discover');
        onLandingPageChanged('WelcomePage');
      }
      if (value === 'QuoteWorkspace') {
        logAnalytics.trackEvent('User preferences', 'Set default landing page', 'pricing');
        onLandingPageChanged('QuoteWorkspace');
      }
    },
    [onLandingPageChanged],
  );
  const onDiscoverPageRegionValueChanged = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      logAnalytics.trackEvent('User preferences', 'Set default discover page region', value);
      onDiscoverPageRegionChanged(value as Region);
    },
    [onDiscoverPageRegionChanged],
  );

  return (
    <TabForm labelKey="userPreferencesForm.general.label" onClose={onClose}>
      <SectionTitle labelKey="userPreferencesForm.displayOptions.label" />
      <FlexRowGroup labelKey="userPreferencesForm.isDarkMode.label" isFirst inputId="theme">
        <div className="d-flex align-items-center">
          <FormattedMessage id="userPreferencesForm.theme.warning" />
          <button
            type="button"
            className="btn btn-flat-primary btn-icon-end ms-2"
            onClick={() => {
              onClose();
              openAccountCenterThemeSwitcher();
            }}
          >
            <FormattedMessage id="userPreferencesForm.theme.link" />
            <i className="icon">open_in_new</i>
          </button>
        </div>
      </FlexRowGroup>

      <>
        <SectionTitle labelKey="userPreferencesForm.quoteWorkspaceVersion.label" />
        <FlexRowGroup labelKey="userPreferencesForm.quoteWorkspaceVersion.default.label" isFirst>
          <ButtonsGroup
            className="w-100"
            items={['Standard', 'Advanced']}
            selectedItems={quoteWorkspaceVersion}
            onSelect={items => onQuoteWorkspaceVersionChanged(items[0])}
            isRadio
            translationPrefix="userPreferencesForm.quoteWorkspaceVersion.values"
          />
        </FlexRowGroup>
      </>

      <>
        <SectionTitle labelKey="userPreferencesForm.batchWorkspaceVersion.label" />
        <FlexRowGroup labelKey="userPreferencesForm.batchWorkspaceVersion.default.label" isFirst>
          <ButtonsGroup
            className="w-100"
            items={['Standard', 'Advanced']}
            selectedItems={batchWorkspaceVersion}
            onSelect={items => onBatchWorkspaceVersionChanged(items[0])}
            isRadio
            translationPrefix="userPreferencesForm.batchWorkspaceVersion.values"
          />
        </FlexRowGroup>
      </>

      {hasWelcomePage && (
        <>
          <SectionTitle labelKey="userPreferencesForm.landingPage.label" />
          <FlexRowGroup labelKey="userPreferencesForm.landingPage.default.label" isFirst>
            <ButtonsGroup
              className="w-100"
              items={['WelcomePage', 'QuoteWorkspace']}
              selectedItems={landingPage ?? 'WelcomePage'}
              onSelect={items => onLandingPageValueChanged(items[0])}
              isRadio
              translationPrefix="userPreferencesForm.landingPage.values"
            />
          </FlexRowGroup>
          {isInternalUser && (
            <>
              <SectionTitle labelKey="userPreferencesForm.discoverPageRegion.label" />
              <FlexRowGroup labelKey="userPreferencesForm.discoverPageRegion.default.label" isFirst>
                <div className="form-group mb-0 me-1">
                  <select
                    data-e2e="region-selector"
                    value={discoverPageRegion ?? 'Switzerland'}
                    onChange={onDiscoverPageRegionValueChanged}
                    className="form-select"
                  >
                    {availableDiscoverPageRegions.map(item => (
                      <option value={item} key={item}>
                        {formatMessage({
                          id: `discover.region.${item}` as IntlKey,
                          defaultMessage: pascalCaseToWords(item),
                        })}
                      </option>
                    ))}
                  </select>
                </div>
              </FlexRowGroup>
            </>
          )}{' '}
        </>
      )}

      {children}
    </TabForm>
  );
}

GeneralTabForm.displayName = 'GeneralTabForm';
